<template>
  <DialogWrapper :fullscreen="isMobile && !confirmDialog" @click-outside="close" :notifyAngularForView="false">
    <GenericDialogLayout :disable-bottom-cta="isMobile && !confirmMessage"
                         :headerText="dialogTitle"
                         :isSubmitBtnOn="confirmDialog || errorNesting"
                         :loading="loading"
                         :submit-button-text="$t(`common.dialog.${errorNesting ? 'ok' : 'confirm'}`)"
                         classes="nesting__dialog"
                         @submit-dialog="submit"
                         @close-dialog="close">
      <template slot="left-side-pane">
        <div v-if="!confirmDialog" class="nesting__dialog--select">
          <div class="nesting__dialog--select__description">
            {{$t(`${nestingDialogText}select.subtitle`, {name: matter.display_name})}}
          </div>
          <ClientAutocomplete :current-client="matter.contacts[0]"
                              :direction="direction"
                              :no-results-text="$t(`${nestingDialogText}select.no_results`)"
                              :placeholder-text="$t(`${nestingDialogText}select.placeholder`)"
                              @client-clicked="openConfirmDialog"/>
        </div>

        <ConfirmNesting :matter-name="matter.display_name"
                        :client-email="originClientEmail"
                        :delete-warning="isLastMatter"
                        :client="client"
                        :active="confirmDialog && !errorNesting"
                        :confirm-message="confirmMessage"
                        :is-mobile="isMobile"
                        :business-term="businessTerm"
        />

        <p data-qa="errorText" v-if="errorNesting">{{errorText}}</p>
      </template>
    </GenericDialogLayout>
  </DialogWrapper>
</template>

<script>
import GenericDialogLayout from "@/modules/common/components/GenericDialogLayout/GenericDialogLayout";
import DialogWrapper from "@/modules/common/components/dialog/DialogWrapper";
import AvatarImage from "@/modules/common/components/AvatarImage/AvatarImage";
import ClientAutocomplete from "@/modules/common/components/ClientAutocomplete/ClientAutocomplete";
import ConfirmNesting from "@/modules/matters/components/Nesting/ConfirmNesting";
import contactService from "@/modules/common/services/contactService";
import {mapGetters} from "vuex";
import {matterService} from "../../services/matterService";
import {clientFieldsService} from "../../../settings/services/clientFieldsService";

export default {
  name: 'Nesting',
  components: {
    ConfirmNesting,
    AvatarImage,
    DialogWrapper,
    GenericDialogLayout,
    ClientAutocomplete
  },
  props: {
    matter: {
      type: Object,
      required: true
    },
    originClientUid: {
      type: String,
      required: true
    },
    originClientEmail: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      confirmDialog: false,
      nestingDialogText: 'client.matters.matter_page.nesting_dialog.',
      client: {},
      errorNesting: false,
      loading: false,
      additionalMatters: []
    }
  },
  computed: {
    ...mapGetters(['isMobile', 'direction']),
    ...mapGetters('clientFields', ['matterFields']),
    ...mapGetters("accountModule", ["businessTerm"]),
    confirmMessage() {
      return this.$t(`${this.nestingDialogText}confirm.message`, {
        matter_full_name: this.matter.display_name,
        destination_contact_full_name: this.client.full_name,
        destination_contact_email: this.clientEmail
      })
    },
    isLastMatter() {
      return this.additionalMatters.filter(matter => !matter.deleted).length < 2;
    },
    dialogTitle() {
      if (this.errorNesting) {
        return this.$t(`${this.nestingDialogText}error.title`)
      }
      return this.$t(`${this.nestingDialogText}${this.confirmDialog ? 'confirm' : 'select'}.title`)
    },
    errorText() {
      return this.$t(`${this.nestingDialogText}error.duplication`, {
        client_fullname: this.client.full_name,
        matter_name: this.matter.display_name
      })
    },
    fieldName() {
      return this.$t('client.add_matter_name_field.field_name', {angularKey:true});
    },
    isMatterNameFiledExist() {
      return this.matterFields && !!this.matterFields.find(filed => filed.type === 'name');
    },
    clientEmail() {
      return this.client.email && `(${this.client.email})`;
    }
  },
  methods: {
    submit() {
      this.errorNesting ? this.close() : this.preformNesting()
    },
    close() {
      this.$emit('close')
    },
    openConfirmDialog(client) {
      this.$track('Nest matter - selected contact');
      this.client = client;
      this.confirmDialog = true;
    },
    async addMatterNameField() {
      await clientFieldsService.addField(
        {
          label: this.fieldName,
          object_type: 'matter',
          type: 'name'
        })
    },
    async getAdditionalMatters() {
      const res = await contactService.getMattersForContact(this.originClientUid);
      if (res.data.error) {
        console.error(res.data.error);
        return;
      }
      this.additionalMatters = res.data.matters;
    },
    async preformNesting() {
      this.loading = true;

      if (!this.isMatterNameFiledExist) {
        await this.addMatterNameField();
        await this.$store.dispatch({type: "clientFields/refreshClientFields"});
        if (!this.isMatterNameFiledExist) return this.nestFail();
      }

      matterService.nestMatter(this.matter.uid, this.client.uid)
        .then(this.nestSuccess)
        .catch(this.nestFail)
    },
    async nestSuccess() {
      this.$track('Nest matter - success');

      this.$frontageApi.send('matter_action', {action: 'set_matter', matter: this.matter, client: this.client});
      await this.$store.dispatch({type: 'mattersModule/fetchMatter', payload: {matterUid: this.matter.uid}});
      await this.$store.dispatch({type: 'mattersModule/fetchContact'})
      this.$frontageApi.navigateTo('app.client', {uid: this.client.uid, matter_uid: this.matter.uid, clearApp: true});
      this.$frontageApi.send('matter_action', {action: 'toast_success', text: this.$t(`${this.nestingDialogText}confirm.toast`)});

      this.close();
    },
    nestFail(err) {
      this.$frontageApi.send('matter_action', {action: 'toast_fail', text: this.$t(`${this.nestingDialogText}error.toast`)});
      this.loading = false;
      if (err && err.status === 422) {
        this.$track('Nest matter - failed since same matter exists');
        this.errorNesting = true;
      } else {
        this.close();
      }
    }
  },
  created() {
    this.getAdditionalMatters();
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

  .nesting__dialog {
    padding-bottom: 0;

    @include md-and-up {
      height: 590px;
      width: 500px;
    }

    .v-dialog--fullscreen & {
      height: 100vh;
    }

    &--select {
      width: 500px;
      height: 370px;

      &__description {
        font-size: 13px;
        margin-bottom: 10px;
      }
    }

    ::v-deep .dialog-footer {
      position: sticky;
    }
  }
</style>
